/**
 * Camera viewport controller — handles drag-pan and scroll-zoom
 * to navigate the game world within the PixiJS canvas.
 */

import { Application, Container } from 'pixi.js';
import { InputMouseKeyboardHandler } from './input_mouse_keyboard_handler';

const MIN_SCALE = 0.25;
const MAX_SCALE = 2.0;
const ZOOM_SPEED = 0.001;

export class CameraViewportController {
  private app: Application;
  private input: InputMouseKeyboardHandler;
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private worldX = 0;
  private worldY = 0;
  private scale = 1.0;

  constructor(app: Application, input: InputMouseKeyboardHandler) {
    this.app = app;
    this.input = input;
  }

  getTransform(): { x: number; y: number; scale: number } {
    return { x: this.worldX, y: this.worldY, scale: this.scale };
  }

  /** Convert screen (canvas) coordinates to world coordinates. */
  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: screenX / this.scale - this.worldX,
      y: screenY / this.scale - this.worldY,
    };
  }

  /** Convert world coordinates to screen (canvas) coordinates. */
  worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    return {
      x: (worldX + this.worldX) * this.scale,
      y: (worldY + this.worldY) * this.scale,
    };
  }

  update(): void {
    const { mouseX, mouseY, mouseDown, mouseButton } = this.input.state;

    // Middle-button or right-button drag to pan
    if (mouseDown && (mouseButton === 1 || mouseButton === 2)) {
      if (!this.isDragging) {
        this.isDragging = true;
        this.dragStartX = mouseX - this.worldX;
        this.dragStartY = mouseY - this.worldY;
      }
      this.worldX = mouseX - this.dragStartX;
      this.worldY = mouseY - this.dragStartY;
    } else {
      this.isDragging = false;
    }

    // Arrow keys pan
    const panSpeed = 5;
    const keys = this.input.state.keys;
    if (keys.has('ArrowLeft') || keys.has('a')) this.worldX += panSpeed;
    if (keys.has('ArrowRight') || keys.has('d')) this.worldX -= panSpeed;
    if (keys.has('ArrowUp') || keys.has('w')) this.worldY += panSpeed;
    if (keys.has('ArrowDown') || keys.has('s')) this.worldY -= panSpeed;

    // Scroll wheel zoom
    const wheelDelta = this.input.state.wheelDelta;
    if (wheelDelta !== 0) {
      const prevScale = this.scale;
      this.scale *= (1 - wheelDelta * ZOOM_SPEED);
      this.scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, this.scale));
      // Zoom toward mouse position
      const scaleChange = this.scale / prevScale;
      this.worldX = mouseX / this.scale - (mouseX / prevScale - this.worldX) / scaleChange;
      this.worldY = mouseY / this.scale - (mouseY / prevScale - this.worldY) / scaleChange;
    }
  }

  /** Transform a stage Container to reflect current camera position. */
  applyTo(stage: Container): void {
    stage.position.set(this.worldX * this.scale, this.worldY * this.scale);
    stage.scale.set(this.scale);
  }
}
